import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import NewsletterForm from "@/components/NewsletterForm";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Database,
  ListChecks,
  BarChart3,
  FileBarChart,
  ShieldCheck,
  Landmark,
  Clock,
  Eye,
  Ban,
  ScanLine,
  ArrowRight,
} from "lucide-react";

export default function Home() {
  const { t } = useI18n();

  const howSteps = ["one", "two", "three", "four"].map((k) => ({
    num: t(`how.steps.${k}_num`),
    title: t(`how.steps.${k}_title`),
    desc: t(`how.steps.${k}_desc`),
  }));
  const stepIcons = [Database, ListChecks, BarChart3, FileBarChart];

  const advantages = [
    { key: "one", Icon: ScanLine },
    { key: "two", Icon: ShieldCheck },
    { key: "three", Icon: Landmark },
    { key: "four", Icon: Clock },
    { key: "five", Icon: Eye },
    { key: "six", Icon: Ban },
  ];

  const aboutCards = [
    { key: "one", Icon: ListChecks },
    { key: "two", Icon: BarChart3 },
    { key: "three", Icon: ShieldCheck },
  ];

  const faqKeys = ["1", "2", "3", "4", "5", "6"];

  return (
    <main className="bg-slate-50 text-slate-900">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
                {t("hero.eyebrow")}
              </span>
              <h1 className="mt-5 font-jakarta text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
                {t("hero.title")}
              </h1>
              <p className="mt-4 text-lg sm:text-xl font-semibold text-slate-700">
                {t("hero.subtitle")}
              </p>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                {t("hero.description")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#newsletter" data-testid="hero-cta-button">
                  <Button className="h-12 px-6 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold w-full sm:w-auto">
                    {t("hero.cta_primary")}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </a>
                <a href="#how" data-testid="hero-cta-secondary">
                  <Button
                    variant="outline"
                    className="h-12 px-6 rounded-md border-slate-300 text-slate-800 hover:bg-slate-50 text-sm font-semibold w-full sm:w-auto"
                  >
                    {t("hero.cta_secondary")}
                  </Button>
                </a>
              </div>

              <dl className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-slate-200 pt-8">
                {["one", "two", "three"].map((k) => (
                  <div key={k}>
                    <dt className="text-sm font-semibold text-slate-900">
                      {t(`hero.trust.${k}_title`)}
                    </dt>
                    <dd className="mt-1 text-sm text-slate-600">
                      {t(`hero.trust.${k}_desc`)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img
                  src="/images/analyst-working.jpg"
                  alt={t("hero.image_alt")}
                  className="w-full h-[380px] lg:h-[520px] object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between text-xs font-mono text-slate-500">
                    <span>ratio_liquidez</span>
                    <span className="text-teal-700">+ estructurado</span>
                  </div>
                  <div className="mt-2 flex items-end gap-1.5 h-14">
                    {[35, 60, 45, 78, 52, 88, 70, 92, 82, 74].map((h, i) => (
                      <span
                        key={i}
                        className="flex-1 rounded-sm bg-teal-500/80"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="/images/business-meeting.jpg"
                  alt={t("about.image_alt")}
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
                {t("about.eyebrow")}
              </span>
              <h2 className="mt-4 font-jakarta text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                {t("about.title")}
              </h2>
              <p className="mt-3 text-lg font-semibold text-slate-700">
                {t("about.subtitle")}
              </p>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                {t("about.paragraph_one")}
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {t("about.paragraph_two")}
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {aboutCards.map(({ key, Icon }) => (
                  <div
                    key={key}
                    className="rounded-xl border border-slate-200 bg-white p-5"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-teal-50 text-teal-700 border border-teal-100">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-slate-900">
                      {t(`about.cards.${key}_title`)}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                      {t(`about.cards.${key}_desc`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20 lg:py-28 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
              {t("how.eyebrow")}
            </span>
            <h2 className="mt-4 font-jakarta text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              {t("how.title")}
            </h2>
            <p className="mt-3 text-lg font-semibold text-slate-700">
              {t("how.subtitle")}
            </p>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              {t("how.description")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {howSteps.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <div
                  key={step.num}
                  className="relative rounded-2xl border border-slate-200 bg-slate-50 p-6"
                  data-testid={`how-step-${i + 1}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-teal-700">
                      {step.num}
                    </span>
                    <Icon className="h-5 w-5 text-slate-400" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-sm text-slate-500 border-l-2 border-teal-500 pl-4">
            {t("how.footnote")}
          </p>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section id="advantages" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
              {t("advantages.eyebrow")}
            </span>
            <h2 className="mt-4 font-jakarta text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              {t("advantages.title")}
            </h2>
            <p className="mt-3 text-lg font-semibold text-slate-700">
              {t("advantages.subtitle")}
            </p>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              {t("advantages.description")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {advantages.map(({ key, Icon }) => (
              <div
                key={key}
                className="rounded-2xl border border-slate-200 bg-white p-6"
                data-testid={`advantage-card-${key}`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-teal-400">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  {t(`advantages.cards.${key}_title`)}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {t(`advantages.cards.${key}_desc`)}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-sm text-slate-500 border-l-2 border-teal-500 pl-4">
            {t("advantages.footnote")}
          </p>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="py-20 lg:py-28 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
                {t("newsletter.eyebrow")}
              </span>
              <h2 className="mt-4 font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                {t("newsletter.title")}
              </h2>
              <p className="mt-3 text-lg font-semibold text-slate-700">
                {t("newsletter.subtitle")}
              </p>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                {t("newsletter.description")}
              </p>
            </div>
            <div className="lg:col-span-3">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
              {t("faq.eyebrow")}
            </span>
            <h2 className="mt-4 font-jakarta text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {t("faq.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600">
              {t("faq.subtitle")}
            </p>
          </div>
          <div className="mt-12">
            <Accordion type="single" collapsible className="space-y-3">
              {faqKeys.map((n) => (
                <AccordionItem
                  key={n}
                  value={`item-${n}`}
                  className="rounded-xl border border-slate-200 bg-white px-5"
                  data-testid={`faq-item-${n}`}
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-slate-900 hover:no-underline py-5">
                    {t(`faq.items.q${n}`)}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-5">
                    {t(`faq.items.a${n}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </main>
  );
}
